import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";
import { PrimeiroMilhaoGraph } from "./PrimeiroMilhaoGraph";
import { PrimeiroMilhaoTable } from "./PrimeiroMilhaoTable";
import { PrimeiroMilhaoPieChart } from "./PrimeiroMilhaoPieChart";

export const PrimeiroMilhaoResponse = ({
  results,
}: {
  results: PrimeiroMilhaoResultsType;
}) => {
  const formatTime = (years: number, months: number) => {
    if (years === 0) {
      return `${months} ${months !== 1 ? "meses" : "mês"}`;
    } else if (months === 0) {
      return `${years} ano${years !== 1 ? "s" : ""}`;
    } else {
      return `${years} ano${years !== 1 ? "s" : ""} e ${months} ${
        months !== 1 ? "meses" : "mês"
      }`;
    }
  };

  return (
    <MainCard className="mt-4">
      <MainCardTitle>Resultado</MainCardTitle>
      
      {/* Resumo em destaque */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border">
        {results.calculationMode === "prazo" ? (
          <div className="text-center">
            <h3 className="text-xl font-bold text-primary mb-2">
              {results.goalReached
                ? `Você atingirá R$ 1 milhão em ${formatTime(
                    results.timeToGoal.years,
                    results.timeToGoal.months
                  )}!`
                : "Meta não atingida no período analisado"}
            </h3>
            {results.goalReached && (
              <p className="text-lg">
                Tempo necessário:{" "}
                <strong className="text-primary">
                  {formatTime(results.timeToGoal.years, results.timeToGoal.months)}
                </strong>
              </p>
            )}
          </div>
        ) : (
          <div className="text-center">
            <h3 className="text-xl font-bold text-primary mb-2">
              Aporte necessário calculado!
            </h3>
            <p className="text-lg">
              Para atingir R$ 1 milhão em{" "}
              <strong className="text-primary">
                {formatTime(results.timeToGoal.years, results.timeToGoal.months)}
              </strong>
            </p>
            <p className="text-lg">
              você precisa investir{" "}
              <strong className="text-xl text-primary">
                R$ {normalSanatizerBRL(results.monthlyPaymentNeeded ?? 0)}
              </strong>{" "}
              por mês
            </p>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-2 grid-cols-1">
        <div className="p-3 bg-primary rounded border flex flex-col justify-center">
          <h2 className="sm:text-base text-sm text-center text-white font-semibold m-0">
            Valor total final
          </h2>
          <p className="sm:text-xl text-center font-bold text-md text-white m-0">
            <span className="text-sm mr-1 text-white">R$</span>
            {normalSanatizerBRL(results.accumulated)}
          </p>
        </div>
        <div className="p-3 bg-white rounded border flex flex-col justify-center">
          <h2 className="sm:text-base text-sm text-center font-semibold m-0">
            Valor total investido
          </h2>
          <p className="sm:text-lg text-center font-black text-md text-red-800 m-0">
            <span className="text-sm mr-1 text-gray-500">R$</span>
            {normalSanatizerBRL(results.invested)}
          </p>
        </div>
        <div className="p-3 bg-white rounded border flex flex-col justify-center">
          <h2 className="sm:text-base text-sm text-center font-semibold m-0">
            Total em juros
          </h2>
          <p className="sm:text-lg text-center font-black text-md text-black m-0">
            <span className="text-sm mr-1 text-gray-500">R$</span>
            {normalSanatizerBRL(results.interest)}
          </p>
        </div>
      </div>

      {/* Informações adicionais específicas do primeiro milhão */}
      {results.calculationMode === "aporte" && results.monthlyPaymentNeeded && (
        <div className="mb-6 p-4 bg-green-50 rounded border">
          <h3 className="font-semibold text-center mb-2">Aporte Mensal Necessário</h3>
          <p className="text-2xl font-bold text-center text-green-700">
            R$ {normalSanatizerBRL(results.monthlyPaymentNeeded)}
          </p>
        </div>
      )}

      {/* Aviso sobre inflação */}
      {results.timeToGoal.years > 1 && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <h4 className="font-semibold text-yellow-800 mb-2 text-sm">
            Considerações Importantes:
          </h4>
          <div className="text-sm text-yellow-700 space-y-1">
            <p>• Este cálculo não considera Imposto de Renda sobre rendimentos</p>
            <p>• A inflação pode reduzir o poder de compra do valor final</p>
            <p>• Rentabilidades podem variar ao longo do tempo</p>
          </div>
        </div>
      )}

      {/* Composição do Primeiro Milhão - Gráfico de Pizza */}
      <h2 className="font-bold text-primary text-lg mt-8 text-center">
        Composição do Primeiro Milhão:
      </h2>
      <div className="mb-6">
        <PrimeiroMilhaoPieChart results={results} />
      </div>

      {/* Evolução no Tempo - Gráfico de Linha */}
      <h2 className="font-bold text-primary text-lg mt-8 text-center">
        Evolução no Tempo:
      </h2>
      <PrimeiroMilhaoGraph results={results} />

      {/* Tabela */}
      <h2 className="font-bold text-primary text-lg my-6 text-center">
        Detalhamento Anual:
      </h2>
      <PrimeiroMilhaoTable results={results} />
    </MainCard>
  );
};